/**
 * How a campaign inclusion is written out, in one place.
 *
 * ## The rule, verbatim from the contract
 *
 * `docs/api/cities-and-inclusions.md` § "The rendering rule — this is the whole
 * point of this master":
 *
 * > Use `unitSingular` when the quantity is exactly 1, and `unitPlural` for
 * > every other quantity. **When both are `null`, render a bare name with no
 * > number.**
 *
 * ```
 * Campus Outreach: 5 campuses     quantity 5  -> unitPlural
 * Profile Evaluation: 1 session   quantity 1  -> unitSingular
 * Priority Support                no unit     -> bare name, NO number
 * ```
 *
 * ## Two things that are easy to get wrong, and were
 *
 * **The no-unit case drops the number entirely.** Not `3 Priority Support` —
 * just `Priority Support`. An inclusion that is simply *present* with no count
 * is expressed by its master carrying no unit, and printing a quantity beside
 * it invents a countable thing the business did not describe. The first draft
 * of this module printed the number, because the brief that produced it
 * contradicted itself; the contract does not.
 *
 * **Nothing here pluralises.** Both forms are stored columns precisely because
 * `campus` becomes `campuses` — not `campuss`, not `campusi` — and no rule
 * worth writing gets there. This text is **printed on a document a customer
 * receives**, so it is told to us rather than computed. Pluralise client-side
 * and the first thing that happens is `1 campuses` on a real quote.
 *
 * ## Why it is a module and not four copies
 *
 * The line editor, the line table, the quote and the invoice all render this.
 * The company stage shipped as three copies in one afternoon and two of them
 * disagreed; four surfaces printing customer-facing text is a worse place to
 * repeat that.
 *
 * ## ⚠️ THIS IS ABOUT TO BECOME A JOIN KEY, NOT A DISPLAY RULE
 *
 * Everything above describes this module as it behaves **today**: four EMERGE
 * surfaces rendering the same text, where the worst case of a change is that
 * four screens agree on something slightly different from what they said
 * yesterday. That is a display rule, and it is why this file reads like one.
 *
 * **It stops being one the moment `LINE_DESCRIPTION_INCLUDES_INCLUSIONS` in
 * `src/lib/line-description.ts` goes `true`.** That file already imports
 * {@link renderInclusion} to build the bracketed inclusions segment —
 * deliberately, so there is one copy of this rule rather than two — and that
 * segment goes onto the **description printed on a customer's Xero quote or
 * invoice**. At that point:
 *
 * - a **second implementation of this exact rule exists in another repository**
 *   (`emerge-backend-app`, composing the same sentence at push time), and
 * - **nothing, on either side, ever compares the two strings.**
 *
 * ### The failure mode, stated plainly
 *
 * There is no error, no failed build, no 4xx and no log line. Both strings are
 * individually well-formed; they simply disagree. The operator reads OUR wording
 * in the description textarea, accepts the line as correct, never types anything
 * — and the customer is invoiced against the BACKEND's wording, which nobody
 * saw. The only symptom is a customer reading something different from what was
 * quoted, about what they were promised.
 *
 * ### What "changing it" includes
 *
 * All of these look like tidy-ups in a diff, and every one of them is breaking
 * once the constant is `true`:
 *
 * - moving the boundary off `quantity === 1` — including "handling" 0 or a
 *   negative, which selects a unit here and may select the other one there
 * - changing the `: ` after the name, or the space between number and unit
 * - printing the NUMBER in the no-unit case (`1 Priority Support`) — the rule at
 *   the top of this file, and the one the first draft got wrong
 * - falling back from a `null` unit to the other form — `unitSingular ??
 *   unitPlural`, or the shape it usually arrives in, *"prefer the singular when
 *   we have one"*:
 *   `quantity === 1 && unitSingular !== null ? unitSingular : unitPlural`.
 *   **This is the single most tempting edit in the file**, because it reads as
 *   making the function robust against a half-configured master. It prints
 *   **`1 campuses`** on a real quote — the exact string this module was written
 *   to make impossible. That second form passes fixture rows `I8` **and
 *   `I9`**, and only `I13` catches it
 * - case-normalising, or collapsing the INTERIOR whitespace of, `name`. Only its
 *   ENDS are trimmed (see below); everything between them is emitted **verbatim,
 *   as stored**, pinned by fixture rows `I14` and `I17`. `I14` carries an
 *   internal capital (`'  MBA fairs  '` → `MBA fairs`) which a title-caser
 *   destroys, and `I17` carries a doubled interior space which a
 *   `replace(/\s+/g, ' ')` destroys.
 *   ⚠️ Every other row in the table
 *   uses a tidy Title-Case name, so a composer that title-cased what it was told
 *   passed all of `I1`–`I13`. Upper- and lower-casing were already caught by
 *   `I4`; title-casing is a no-op on every other name, and it is the form the
 *   reflex actually takes
 * - ⚠️ **NOT trimming `unitSingular` / `unitPlural`, or `name`.** All three are
 *   trimmed, matching the backend peer, who composes the same sentence. Written
 *   `!== null` on a unit — or raw on a name — the two systems differ by
 *   invisible characters on a customer's document, with nothing comparing them.
 *   `I15` pins the trimmed unit, `I14` the trimmed name
 * - ⚠️ **Reading the two trims as ONE rule.** They are trimmed the same way and
 *   used differently: an emptied UNIT is *absent* and drops the number with it,
 *   an emptied NAME is still emitted — `': 2 campuses'`, pinned by `I18` and
 *   `I19`. Both sides do exactly that, so "treat a blank name as absent too" is
 *   a divergence, not a tidy-up
 * - rendering `quantity` through a number formatter rather than interpolating it.
 *   `toLocaleString()` prints `1,500 campuses`; pinned by `I16`, whose absurd
 *   quantity is the point — at 1 and 2 every formatter agrees with raw
 *   interpolation, so nothing below four figures can tell them apart
 * - adding pluralisation of any kind (see below)
 *
 * **If you change any of it, tell the emerge-backend-app peer in the same
 * breath** — the two sides have to move together, and there is no build, lint or
 * test on either side that will notice if they do not.
 *
 * ### ⚠️ The stored columns are exactly why this survives being a join key
 *
 * The two-column design is not verbosity, and it is what makes agreement between
 * two independent implementations *achievable at all*: `campus` → `campuses`
 * follows no rule worth writing — not `campuss`, not `campusi` — so the wording
 * is **told to both sides by the same database row** rather than derived twice.
 * Two composers reading one stored string cannot disagree about it. Two
 * composers each running their own pluraliser will, on the first irregular noun
 * somebody adds, and the first thing a code-side pluraliser does is print
 * `1 campuses` on a document a customer receives. A pluraliser here would
 * therefore not merely be wrong — it would reintroduce the divergence the stored
 * columns exist to prevent, in the one place nothing checks.
 *
 * The same goes for the null-unit rule: it is a **contract** rule, from
 * `docs/api/cities-and-inclusions.md`, which is what lets the backend implement
 * it from the same sentence rather than from our source.
 *
 * ### Where the rest of this is written down
 *
 * `src/lib/line-description.ts` is the file that owns the join. Read its header
 * before changing anything here: it carries the separator, the bracket
 * constants, the ordering rule, the flip condition on
 * `LINE_DESCRIPTION_INCLUDES_INCLUSIONS`, and the shared `I`-numbered fixture
 * table that pins the agreed output — including the three rows `I8`, `I9` and
 * `I13`, which pin **this** module's null-unit behaviour and must be read
 * together, and `I14`–`I21`, which pin how the three stored strings and the
 * number are emitted: the two units and the name **trimmed at the ends** and
 * otherwise exactly as given, the number raw, and an emptied NAME still printed
 * where an emptied UNIT is absent.
 *
 * ### ⚠️ How rows get added here: enumerate the WRONG implementations
 *
 * Every one of those rows exists because somebody wrote a plausible **wrong**
 * version of this function and ran it. `I13` is the case that makes the argument:
 * the most natural defensive rewrite of the rule below — *"prefer the singular
 * when we have one"* — passes `I8` **and** `I9`, and `I13` is the only row in
 * either table that catches it. Nothing about the correct implementation suggests
 * `I13` exists, so no amount of testing the right implementation would have found
 * it. The full method, and the candidate-by-candidate results for both tables,
 * are written up in `line-description.ts`; the short version is that **a shared
 * fixture is only as good as its worst-covered edge, and the edges you think of
 * are the ones your own implementation suggested.**
 *
 * The backend asserts its own composer against the same rows — a shared
 * fixture pinning only one side is exactly the state this file's warning is
 * about.
 */

import type { LineInclusion } from './types';

/**
 * The unit for a quantity, TRIMMED, or null when that side is unset — where
 * "unset" means `null`, `''` **or whitespace-only**.
 *
 * ⚠️ **The trim is an AGREEMENT, not a preference.** The backend composes
 * this same sentence and trims; written
 * `!== null` here, a whitespace-only unit emits `Campus Outreach: 2   ` inside
 * the parentheses on a document a customer is invoiced against, while their
 * copy emits the bare name — the two systems differing by invisible characters,
 * with nothing anywhere comparing them.
 *
 * The argument that settled it came from this repo rather than from them:
 * `line-description.ts` already treats a whitespace-only project name and item
 * code as absent, **in the same composed string**. Two whitespace conventions
 * inside one sentence is the incoherence nobody notices until it is on a
 * customer's document.
 *
 * 🔴 **CORRECTED 2026-08-31: the NAME IS TRIMMED TOO — see `renderInclusion`.**
 * *SUPERSEDED: "⚠️ **The NAME is still emitted verbatim — see `renderInclusion`.
 * That asymmetry is deliberate: a name is content, a unit is grammar.** An admin
 * who types `MBA fairs` with an internal capital meant it; nobody means a unit
 * with a trailing space."*
 *
 * ⚠️ **That reasoning was about CASE and INTERIOR characters, and it is still
 * right about those — it was never an argument for keeping trailing spaces.**
 * "A name is content" says do not re-word it; it does not say a name means its
 * own padding. Nobody means a *name* with a trailing space either, and the
 * backend's `inclusionText` has opened `inclusion.name.trim()` throughout.
 *
 * So the frontend moved, not the backend: **the pushed document is what the
 * customer receives, so it is the authoritative string.** Aligning the preview
 * to it changes no customer-facing document; making the backend stop trimming
 * would have altered live output to match a preview nobody had complained
 * about. Fixture row `I14` pins the trimmed name and `I15` the trimmed unit.
 *
 * ⚠️ **One asymmetry survives and is NOT the one above.** Both strings are
 * trimmed identically; what differs is what an EMPTY result means. An emptied
 * unit is **absent** — the bare name prints and the number goes with it. An
 * emptied name is **still emitted**, so a whitespace-only name prints
 * `': 2 campuses'`. That is what the backend does, and `I18` / `I19` pin it.
 *
 * The TRIMMED value is what gets emitted, not the raw one — otherwise both
 * sides would agree on which branch to take and still produce different bytes.
 */
export function inclusionUnit(
  inclusion: Pick<LineInclusion, 'unitSingular' | 'unitPlural'>,
  quantity: number,
): string | null {
  /*
   * Chosen by quantity FIRST, then tested — never `unitSingular ?? unitPlural`,
   * and never "prefer the singular when we have one". See this file's header:
   * that second form prints `1 campuses` and passes every fixture row but I13.
   */
  const raw = quantity === 1 ? inclusion.unitSingular : inclusion.unitPlural;
  const unit = raw?.trim() ?? '';
  return unit === '' ? null : unit;
}

/**
 * One inclusion as it prints on a document.
 *
 * `Campus Outreach: 5 campuses`, or the bare `Priority Support` when the master
 * records no unit for that quantity — in which case **no number appears at
 * all**, which is the contract's rule and not an omission.
 *
 * ⚠️ **This is the function `line-description.ts` calls to build the customer's
 * document line.** Every byte it returns — the `: `, the space before the unit,
 * which unit, and whether a number appears at all — is pinned against a second
 * implementation in `emerge-backend-app` that nothing compares it with. Read
 * this file's header before changing it, and tell the backend peer if you do.
 *
 * ⚠️ **The null-unit test is per QUANTITY, not per inclusion.** A master with
 * `unitSingular: 'campus'` and `unitPlural: null` prints `Campus Outreach: 1
 * campus` at one and the bare `Campus Outreach` at two — the unit is chosen by
 * the quantity *first*, and only then tested for `null`. An implementation
 * asking `unitSingular === null && unitPlural === null` looks equivalent and is
 * not; the shared fixture rows `I9` and `I13` exist to catch exactly that.
 *
 * ⚠️ **The NAME IS TRIMMED, and that is an AGREEMENT with the backend rather
 * than a tidy-up.** Their `inclusionText` opens `const name =
 * inclusion.name.trim()`, so a stored name padded at either end printed one way
 * here and another on the customer's Xero document — same branch, different
 * bytes, nothing comparing them. **The frontend moved because the PUSHED
 * document is the one the customer receives and is therefore authoritative**:
 * aligning the preview to it changes no customer-facing document, where making
 * the backend stop trimming would have altered live output.
 *
 * ⚠️ **Only the ENDS.** `.trim()` and nothing else — no case-normalising, no
 * collapsing of interior whitespace, no filtering. `'  MBA  fairs  '` prints
 * `MBA  fairs`, doubled space intact (`I17`), and a name that trims to `''` is
 * still emitted, giving `': 2 campuses'` (`I18`, `I19`) — an emptied NAME is not
 * absent the way an emptied UNIT is.
 */
export function renderInclusion(
  inclusion: Pick<LineInclusion, 'name' | 'unitSingular' | 'unitPlural'>,
  quantity: number,
): string {
  const name = inclusion.name.trim();
  const unit = inclusionUnit(inclusion, quantity);
  return unit === null ? name : `${name}: ${quantity} ${unit}`;
}

/** The same rule for an inclusion that already carries its own quantity. */
export function inclusionLine(inclusion: LineInclusion): string {
  return renderInclusion(inclusion, inclusion.quantity);
}

/**
 * Whether a quantity may be saved.
 *
 * **Zero is refused** — the join table is `NOT NULL DEFAULT 1` with a
 * `CHECK (quantity >= 1)`. "0 campuses" reads as a promise of nothing, and an
 * inclusion with no count is expressed by its master carrying no unit, never by
 * a quantity of zero. Omitting the quantity means 1; it is the explicit zero
 * that is rejected.
 */
export function isValidInclusionQuantity(quantity: number): boolean {
  return Number.isInteger(quantity) && quantity >= 1;
}
